import { useEffect, useState } from 'react';
import {
  GOOGLE_MESSAGE_SOURCE,
  LOGIN_PATH,
  openGoogleChannel,
  parseGoogleCallbackParams,
  takeReturnSearch,
} from '../services/GoogleLogin';
import { useLocale } from '../i18n/LocaleContext';

// Where Google redirects after the user picks an account. It verifies nothing
// itself: the state/nonce that prove this response belongs to a real click
// live in the sessionStorage of the window that opened the popup, which this
// one cannot see. So it only relays the raw parameters there and gets out of
// the way.
//
// Two shapes to handle:
//   • popup (the normal path) → relay, wait for the login window's ack, close.
//   • no popup (the browser blocked it, so the flow ran in this very tab) →
//     nobody is listening, so bounce back to the login screen with the
//     fragment intact and let it finish the sign-in.
//
// The ack is what separates the two: without it we'd bounce inside a popup
// that has no session state, and the user would watch a successful sign-in
// turn into a bogus failure.
const ACK_WAIT_MS = 1200;

export default function GoogleCallback() {
  const { t } = useLocale();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const params = parseGoogleCallbackParams();
    const message = { source: GOOGLE_MESSAGE_SOURCE, ...params };
    const channel = openGoogleChannel();
    let bounceTimer = null;
    let stuckTimer = null;

    function isAck(data) {
      return data?.source === GOOGLE_MESSAGE_SOURCE && data?.ack === true;
    }

    function onAck() {
      if (bounceTimer) clearTimeout(bounceTimer);
      bounceTimer = null;
      channel?.close();
      window.close();
      // window.close() is a no-op for anything the script didn't open, so if
      // the window is still standing a moment later, tell the user what to do.
      stuckTimer = setTimeout(() => setStuck(true), 400);
    }

    function onWindowMessage(event) {
      if (event.origin !== window.location.origin) return;
      if (isAck(event.data)) onAck();
    }

    if (channel) {
      channel.onmessage = (event) => {
        if (isAck(event.data)) onAck();
      };
      channel.postMessage(message);
    }
    // Fallback for browsers without BroadcastChannel — and a second chance if
    // the broadcast is ever dropped. Harmless when it already landed: the
    // login window ignores a repeat delivery of the same result.
    window.addEventListener('message', onWindowMessage);
    if (window.opener && !window.opener.closed && window.opener !== window) {
      window.opener.postMessage(message, window.location.origin);
    }

    bounceTimer = setTimeout(() => {
      channel?.close();
      window.location.replace(
        `${LOGIN_PATH}${takeReturnSearch()}${window.location.hash}`
      );
    }, ACK_WAIT_MS);

    return () => {
      if (bounceTimer) clearTimeout(bounceTimer);
      if (stuckTimer) clearTimeout(stuckTimer);
      window.removeEventListener('message', onWindowMessage);
      channel?.close();
    };
  }, []);

  // Renders for a blink at most — either this window closes or it redirects.
  if (!stuck) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        textAlign: 'center',
        fontFamily: "'Open Sans', sans-serif",
        fontSize: 16,
      }}
    >
      {t('login.googleCloseWindow')}
    </div>
  );
}
